import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { Link } from "react-router";
import {
  ArrowDownIcon,
  ArrowUpIcon,
  EqualIcon,
  ExternalLinkIcon,
  Loader2Icon,
  PlusIcon,
  SparklesIcon,
} from "lucide-react";
import { Button } from "@/shared/components/ui/button";
import { Checkbox } from "@/shared/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/shared/components/ui/dialog";
import { cn } from "@/shared/lib/utils";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/shared/components/ui/popover";
import { isColumnPrompted } from "@/domains/matrix";
import { COLUMN_TYPE_ICONS } from "../lib/column-type-meta";
import type {
  MatrixColumnDef,
  MatrixColumnType,
  MatrixRowUi,
} from "@/domains/matrix";
import { MatrixCell } from "./matrix-cell";
import { ColumnHeaderPopover } from "./column-header-popover";
import { UnsavedChangesDialog } from "./unsaved-changes-dialog";
import { isOptimisticRow } from "../lib/optimistic-row";
import type { BadgeColorMap } from "../lib/value-badge";
import { SCROLL_FADE_BOTTOM, SCROLL_FADE_HEIGHT } from "../lib/scroll-fade";
import type {
  AdvanceDirection,
  CellAddress,
  ColumnPopoverTarget,
  SortState,
} from "../types";
import { useGridKeyboardNavigation } from "../hooks/use-grid-keyboard-navigation";

const MIN_COLUMN_WIDTH = 80;

/**
 * A select in the column editor renders its list in a portal, so picking an
 * option counts as an interaction outside the popover, and the list is gone by
 * the time the popover asks. Hence a grace period rather than a lookup.
 */
const NESTED_SELECT_GRACE_MS = 300;

// The click answering the dialog reaches the editor again once its layer is gone.
const CONFIRM_ANSWER_GRACE_MS = 300;

type ColumnSubmitValues = {
  label: string;
  type: MatrixColumnType;
  displayDescription: string;
};

type MatrixGridProps = {
  columns: MatrixColumnDef[];
  rows: MatrixRowUi[];
  badgeColorsByColumn: Map<string, BadgeColorMap>;
  selectedRowIds: Set<string>;
  sortState: SortState;
  selectedCell: CellAddress | null;
  editingCell: CellAddress | null;
  columnPopover: ColumnPopoverTarget | null;
  isSubmittingColumn: boolean;
  columnSubmitError: string | null;
  onOpenColumnPopover: (target: ColumnPopoverTarget | null) => void;
  onToggleRow: (analysisId: string) => void;
  onToggleAllRows: () => void;
  onSort: (columnKey: string) => void;
  onSelectCell: (address: CellAddress | null) => void;
  onStartEdit: (address: CellAddress) => void;
  onCommitEdit: (
    address: CellAddress,
    value: string,
    nextAddress?: CellAddress | null,
  ) => void;
  onCancelEdit: () => void;
  onExpandCell: (address: CellAddress) => void;
  onResizeColumn: (columnKey: string, width: number) => void;
  onAddRow: () => void;
  isAddingRow: boolean;
  hasActiveRows: boolean;
  onSubmitColumn: (
    columnKey: string | null,
    values: ColumnSubmitValues,
  ) => void;
  onRemoveColumn: (columnKey: string) => void;
};

type ColGroupProps = {
  columns: MatrixColumnDef[];
  widthFor: (column: MatrixColumnDef) => number;
};

// Shared by the header and body tables, which must lay out identically.
function MatrixColGroup({ columns, widthFor }: ColGroupProps) {
  return (
    <colgroup>
      <col style={{ width: 40 }} />
      <col style={{ width: 40 }} />
      {columns.map((column) => (
        <col key={column.key} style={{ width: widthFor(column) }} />
      ))}
      <col style={{ width: 120 }} />
      <col />
    </colgroup>
  );
}

export function MatrixGrid({
  columns,
  rows,
  badgeColorsByColumn,
  selectedRowIds,
  sortState,
  selectedCell,
  editingCell,
  columnPopover,
  isSubmittingColumn,
  columnSubmitError,
  onOpenColumnPopover,
  onToggleRow,
  onToggleAllRows,
  onSort,
  onSelectCell,
  onStartEdit,
  onCommitEdit,
  onCancelEdit,
  onExpandCell,
  onResizeColumn,
  onAddRow,
  isAddingRow,
  hasActiveRows,
  onSubmitColumn,
  onRemoveColumn,
}: MatrixGridProps) {
  const tableRef = useRef<HTMLDivElement>(null);
  const headerScrollRef = useRef<HTMLDivElement>(null);
  const bodyScrollRef = useRef<HTMLDivElement>(null);
  const resizeStateRef = useRef<{
    columnKey: string;
    startX: number;
    startWidth: number;
    currentWidth: number;
  } | null>(null);
  const resizeCleanupRef = useRef<(() => void) | null>(null);
  const pendingScrollToBottomRef = useRef(false);
  const verticalDividerRef = useRef<HTMLDivElement>(null);
  const horizontalDividerRef = useRef<HTMLDivElement>(null);
  const bottomFadeRef = useRef<HTMLDivElement>(null);
  const nestedSelectRef = useRef({ isOpen: false, closedAt: 0 });
  const columnEditorDirtyRef = useRef(false);
  // Radix fires the focus handler and the shared one for the same event.
  const isFocusMoveRef = useRef(false);
  const confirmAnsweredAtRef = useRef(0);
  const [isConfirmingColumnClose, setIsConfirmingColumnClose] = useState(false);
  const [columnKeyPendingRemoval, setColumnKeyPendingRemoval] = useState<
    string | null
  >(null);
  const [resizingColumnKey, setResizingColumnKey] = useState<string | null>(
    null,
  );
  const [resizingWidth, setResizingWidth] = useState(0);

  function handleColumnEditorDirtyChange(isDirty: boolean) {
    columnEditorDirtyRef.current = isDirty;
  }

  function handleNestedSelectOpenChange(isOpen: boolean) {
    nestedSelectRef.current = isOpen
      ? { isOpen: true, closedAt: 0 }
      : { isOpen: false, closedAt: Date.now() };
  }

  function keepOpenForSelectPortals(event: { preventDefault: () => void }) {
    const isFocusMove = isFocusMoveRef.current;
    isFocusMoveRef.current = false;
    const { isOpen, closedAt } = nestedSelectRef.current;
    if (isOpen || Date.now() - closedAt < NESTED_SELECT_GRACE_MS) {
      event.preventDefault();
      return;
    }
    if (isConfirmingColumnClose || columnKeyPendingRemoval !== null) {
      event.preventDefault();
      return;
    }
    if (Date.now() - confirmAnsweredAtRef.current < CONFIRM_ANSWER_GRACE_MS) {
      event.preventDefault();
      return;
    }
    if (columnEditorDirtyRef.current) {
      event.preventDefault();
      // A closing dialog returns focus outside the editor: focus alone never asks.
      if (isFocusMove) return;
      setIsConfirmingColumnClose(true);
    }
  }

  function keepOpenOnFocusMove(event: { preventDefault: () => void }) {
    isFocusMoveRef.current = true;
    event.preventDefault();
  }

  function requestColumnPopover(next: ColumnPopoverTarget | null) {
    if (hasActiveRows && next) return;
    if (columnEditorDirtyRef.current) {
      setIsConfirmingColumnClose(true);
      return;
    }
    onOpenColumnPopover(next);
  }

  function answerConfirmation() {
    confirmAnsweredAtRef.current = Date.now();
    setIsConfirmingColumnClose(false);
  }

  function discardColumnEditor() {
    confirmAnsweredAtRef.current = Date.now();
    columnEditorDirtyRef.current = false;
    setIsConfirmingColumnClose(false);
    onOpenColumnPopover(null);
  }

  const allSelected = rows.length > 0 && selectedRowIds.size === rows.length;
  const someSelected =
    selectedRowIds.size > 0 && selectedRowIds.size < rows.length;

  const orderedRows = (() => {
    if (!sortState) return rows;
    const { columnKey, direction } = sortState;
    return [...rows].sort((a, b) => {
      const left = (a.cells[columnKey]?.value ?? "").trim();
      const right = (b.cells[columnKey]?.value ?? "").trim();
      if (!left && !right) return 0;
      if (!left) return 1;
      if (!right) return -1;
      const compared = left.localeCompare(right);
      return direction === "asc" ? compared : -compared;
    });
  })();

  function handleResizeStart(
    columnKey: string,
    startX: number,
    startWidth: number,
  ) {
    if (hasActiveRows) return;
    resizeStateRef.current = {
      columnKey,
      startX,
      startWidth,
      currentWidth: startWidth,
    };
    setResizingColumnKey(columnKey);
    setResizingWidth(startWidth);

    function onMouseMove(e: MouseEvent) {
      const state = resizeStateRef.current;
      if (!state) return;
      const delta = e.clientX - state.startX;
      const newWidth = Math.max(MIN_COLUMN_WIDTH, state.startWidth + delta);
      state.currentWidth = newWidth;
      setResizingWidth(newWidth);
    }

    function detachResizeListeners() {
      resizeCleanupRef.current = null;
      document.removeEventListener("mousemove", onMouseMove);
      document.removeEventListener("mouseup", onMouseUp);
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
    }

    function onMouseUp() {
      const state = resizeStateRef.current;
      resizeStateRef.current = null;
      setResizingColumnKey(null);
      detachResizeListeners();
      if (state) onResizeColumn(state.columnKey, state.currentWidth);
    }

    document.addEventListener("mousemove", onMouseMove);
    document.addEventListener("mouseup", onMouseUp);
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";
    resizeCleanupRef.current = detachResizeListeners;
  }

  useEffect(() => {
    return () => {
      resizeStateRef.current = null;
      resizeCleanupRef.current?.();
    };
  }, []);

  // Keeps header padding, dividers and fade in step with the measured scrollbars.
  function syncScrollbarChrome() {
    const body = bodyScrollRef.current;
    const header = headerScrollRef.current;
    if (!body || !header) return;
    const verticalWidth = body.offsetWidth - body.clientWidth;
    const horizontalHeight = body.offsetHeight - body.clientHeight;

    header.style.paddingRight = `${verticalWidth}px`;

    const verticalDivider = verticalDividerRef.current;
    if (verticalDivider) {
      verticalDivider.style.right = `${verticalWidth}px`;
      verticalDivider.style.bottom = `${horizontalHeight}px`;
      verticalDivider.hidden = verticalWidth === 0;
    }

    const horizontalDivider = horizontalDividerRef.current;
    if (horizontalDivider) {
      horizontalDivider.style.bottom = `${horizontalHeight}px`;
      horizontalDivider.style.right = `${verticalWidth}px`;
      horizontalDivider.hidden = horizontalHeight === 0;
    }

    const bottomFade = bottomFadeRef.current;
    if (bottomFade) {
      const atBottom =
        body.scrollTop + body.clientHeight >= body.scrollHeight - 1;
      bottomFade.style.bottom = `${horizontalHeight}px`;
      bottomFade.style.right = `${verticalWidth}px`;
      bottomFade.hidden = atBottom;
    }
  }

  useEffect(syncScrollbarChrome);

  useEffect(() => {
    const body = bodyScrollRef.current;
    if (!body) return;
    const observer = new ResizeObserver(syncScrollbarChrome);
    observer.observe(body);
    return () => observer.disconnect();
  }, []);

  function handleBodyScroll(event: React.UIEvent<HTMLDivElement>) {
    const header = headerScrollRef.current;
    if (header) header.scrollLeft = event.currentTarget.scrollLeft;
    syncScrollbarChrome();
  }

  function handleAddRow() {
    pendingScrollToBottomRef.current = true;
    onAddRow();
  }

  useLayoutEffect(() => {
    if (!pendingScrollToBottomRef.current) return;
    pendingScrollToBottomRef.current = false;
    const body = bodyScrollRef.current;
    if (body) body.scrollTop = body.scrollHeight;
  });

  useGridKeyboardNavigation({
    orderedRows,
    columns,
    selectedCell,
    editingCell,
    onSelectCell,
    onStartEdit,
    onCancelEdit,
    onExpandCell,
  });

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (!tableRef.current || tableRef.current.contains(e.target as Node)) {
        return;
      }
      // mousedown runs before blur: tearing the editor down here drops the value.
      if (editingCell) return;
      onSelectCell(null);
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [onSelectCell, editingCell]);

  function widthFor(column: MatrixColumnDef) {
    return column.key === resizingColumnKey ? resizingWidth : column.width;
  }

  // w-0 min-w-full and min-h-0: the grid scrolls, never the page.
  return (
    <div
      ref={tableRef}
      className="border-border/80 bg-background flex min-h-0 w-0 min-w-full flex-col overflow-hidden rounded-xl border shadow-sm"
    >
      <div
        ref={headerScrollRef}
        className="border-border/80 bg-muted shrink-0 overflow-hidden border-b"
      >
        <table
          className="w-full border-collapse text-sm"
          style={{ tableLayout: "fixed" }}
        >
          <MatrixColGroup columns={columns} widthFor={widthFor} />
          <thead>
            <tr className="bg-muted/25">
              <th className="border-border/60 bg-muted border-r px-3 py-2.5 text-center last:border-r-0">
                <Checkbox
                  checked={
                    allSelected ? true : someSelected ? "indeterminate" : false
                  }
                  onCheckedChange={onToggleAllRows}
                  aria-label="Select all rows"
                />
              </th>
              <th className="border-border/60 text-muted-foreground bg-muted w-12 border-r px-2 py-2.5 text-center text-xs font-medium tabular-nums last:border-r-0">
                Nr.
              </th>
              {columns.map((column) => {
                const Icon = COLUMN_TYPE_ICONS[column.type];
                const isSorted = sortState?.columnKey === column.key;
                const sortDirection = isSorted ? sortState.direction : null;
                const sortLabel =
                  sortDirection === "asc"
                    ? `Sorted A to Z`
                    : sortDirection === "desc"
                      ? `Sorted Z to A`
                      : `Not sorted by ${column.label}`;
                const isPopoverOpen =
                  columnPopover?.mode === "edit" &&
                  columnPopover.columnKey === column.key;

                return (
                  <th
                    key={column.key}
                    className="border-border/60 text-muted-foreground bg-muted relative border-r p-0 text-left text-xs font-medium last:border-r-0"
                  >
                    <Popover
                      open={isPopoverOpen}
                      onOpenChange={(open) =>
                        requestColumnPopover(
                          open ? { mode: "edit", columnKey: column.key } : null,
                        )
                      }
                    >
                      <div className="relative flex min-w-0 items-center">
                        <button
                          type="button"
                          data-sorted={isSorted}
                          className={cn(
                            "focus-visible:ring-ring/50 hover:text-primary hover:bg-foreground/8 absolute top-1/2 left-2 z-10 flex size-6 -translate-y-1/2 items-center justify-center rounded-md transition-colors focus-visible:ring-[3px] focus-visible:outline-none",
                            isSorted
                              ? "text-primary bg-primary/10"
                              : "text-muted-foreground/60",
                          )}
                          aria-label={`${sortLabel}. Click to change sorting.`}
                          title={sortLabel}
                          onClick={() => onSort(column.key)}
                          disabled={hasActiveRows}
                        >
                          {sortDirection === "asc" ? (
                            <ArrowUpIcon className="size-3" />
                          ) : sortDirection === "desc" ? (
                            <ArrowDownIcon className="size-3" />
                          ) : (
                            <EqualIcon className="size-3" />
                          )}
                        </button>
                        <PopoverTrigger asChild>
                          <button
                            type="button"
                            className="hover:bg-muted hover:text-foreground focus-visible:ring-ring data-[state=open]:bg-muted data-[state=open]:text-foreground flex min-w-0 flex-1 items-center gap-1.5 rounded-md py-2.5 pr-2 pl-10 text-left text-xs font-medium transition-colors focus-visible:ring-[3px] focus-visible:outline-none"
                            aria-label={`Edit column ${column.label}`}
                            disabled={hasActiveRows}
                          >
                            <Icon className="size-3.5 shrink-0 opacity-50" />
                            <span className="truncate">{column.label}</span>
                            {isColumnPrompted(column) ? (
                              <SparklesIcon
                                className="text-primary ml-auto size-3 shrink-0 mr-1.5"
                                aria-label="Enriched column"
                              />
                            ) : null}
                          </button>
                        </PopoverTrigger>
                      </div>
                      <PopoverContent
                        className="w-[22rem] overflow-hidden rounded-xl p-0"
                        align="start"
                        onInteractOutside={keepOpenForSelectPortals}
                        onFocusOutside={keepOpenOnFocusMove}
                        onEscapeKeyDown={keepOpenForSelectPortals}
                      >
                        {isPopoverOpen ? (
                          <ColumnHeaderPopover
                            mode="edit"
                            column={column}
                            allColumns={columns}
                            isSubmitting={isSubmittingColumn}
                            submitError={columnSubmitError}
                            onNestedSelectOpenChange={
                              handleNestedSelectOpenChange
                            }
                            onDirtyChange={handleColumnEditorDirtyChange}
                            onSubmit={(values) =>
                              onSubmitColumn(column.key, values)
                            }
                            onRemove={() =>
                              setColumnKeyPendingRemoval(column.key)
                            }
                            disabled={hasActiveRows}
                          />
                        ) : null}
                      </PopoverContent>
                    </Popover>
                    <div
                      role="separator"
                      aria-orientation="vertical"
                      aria-label={`Resize column ${column.label}`}
                      aria-disabled={hasActiveRows}
                      className="hover:bg-primary/30 absolute top-0 right-0 z-10 h-full w-1 cursor-col-resize aria-disabled:pointer-events-none aria-disabled:cursor-default"
                      onMouseDown={(e) =>
                        handleResizeStart(column.key, e.clientX, column.width)
                      }
                    />
                  </th>
                );
              })}
              <th className="bg-muted px-3 py-2.5">
                <Popover
                  open={columnPopover?.mode === "create"}
                  onOpenChange={(open) =>
                    requestColumnPopover(
                      open ? { mode: "create", columnKey: null } : null,
                    )
                  }
                >
                  <PopoverTrigger asChild>
                    <button
                      type="button"
                      className="text-muted-foreground hover:text-foreground focus-visible:ring-ring/50 flex items-center gap-1 rounded-md px-1 py-0.5 text-xs font-medium transition-colors focus-visible:ring-[3px] focus-visible:outline-none"
                      disabled={hasActiveRows}
                    >
                      <PlusIcon className="size-3.5" />
                      Add column
                    </button>
                  </PopoverTrigger>
                  <PopoverContent
                    className="w-[22rem] overflow-hidden rounded-xl p-0"
                    align="end"
                    onInteractOutside={keepOpenForSelectPortals}
                    onFocusOutside={keepOpenOnFocusMove}
                    onEscapeKeyDown={keepOpenForSelectPortals}
                  >
                    {columnPopover?.mode === "create" ? (
                      <ColumnHeaderPopover
                        mode="create"
                        allColumns={columns}
                        isSubmitting={isSubmittingColumn}
                        submitError={columnSubmitError}
                        onSubmit={(values) => onSubmitColumn(null, values)}
                        onNestedSelectOpenChange={handleNestedSelectOpenChange}
                        onDirtyChange={handleColumnEditorDirtyChange}
                        disabled={hasActiveRows}
                      />
                    ) : null}
                  </PopoverContent>
                </Popover>
              </th>
              <th className="bg-muted" />
            </tr>
          </thead>
        </table>
      </div>

      <div className="relative flex min-h-0 flex-col">
        <div
          ref={verticalDividerRef}
          aria-hidden
          className="bg-border/80 pointer-events-none absolute top-0 z-10 w-px"
        />
        <div
          ref={horizontalDividerRef}
          aria-hidden
          className="bg-border/80 pointer-events-none absolute left-0 z-10 h-px"
        />
        <div
          ref={bottomFadeRef}
          aria-hidden
          className="pointer-events-none absolute left-0 z-10"
          style={{
            height: SCROLL_FADE_HEIGHT,
            background: SCROLL_FADE_BOTTOM,
          }}
        />
        <div
          ref={bodyScrollRef}
          onScroll={handleBodyScroll}
          className="min-h-0 flex-1 overflow-auto"
        >
          <table
            className="w-full border-collapse text-sm"
            style={{ tableLayout: "fixed" }}
          >
            <MatrixColGroup columns={columns} widthFor={widthFor} />
            <tbody>
              {orderedRows.map((row, index) => (
                <tr
                  key={row.analysisId}
                  className={`hover:bg-muted/15 transition-colors ${
                    selectedRowIds.has(row.analysisId) ? "bg-primary/5" : ""
                  }`}
                >
                  <td
                    className={cn(
                      "border-border/80 border-r px-3 py-2.5 text-center align-middle",
                      index < orderedRows.length - 1 && "border-b",
                    )}
                  >
                    <Checkbox
                      checked={selectedRowIds.has(row.analysisId)}
                      onCheckedChange={() => onToggleRow(row.analysisId)}
                      aria-label={`Select row ${index + 1}`}
                    />
                  </td>
                  <td
                    className={cn(
                      "border-border/80 text-muted-foreground relative border-r px-2 py-2.5 text-center text-xs tabular-nums",
                      index < orderedRows.length - 1 && "border-b",
                    )}
                  >
                    <span className="row-hovered:opacity-0 block transition-opacity">
                      {index + 1}
                    </span>
                    {isOptimisticRow(row.analysisId) ? null : (
                      <Button
                        asChild
                        variant="outline"
                        size="icon-sm"
                        aria-label={`Open report for row ${index + 1}`}
                        title="Open report"
                        className="text-muted-foreground hover:text-primary hover:bg-surface-hover border-border/70 row-hovered:opacity-100 absolute top-1/2 left-1/2 size-6 -translate-x-1/2 -translate-y-1/2 opacity-0 focus-visible:opacity-100"
                      >
                        <Link to={`/reports/${row.analysisId}`}>
                          <ExternalLinkIcon className="size-3.5" />
                        </Link>
                      </Button>
                    )}
                  </td>
                  {columns.map((column, columnIndex) => {
                    const address: CellAddress = {
                      analysisId: row.analysisId,
                      columnKey: column.key,
                    };
                    const cell = row.cells[column.key];
                    const isSelected =
                      selectedCell?.analysisId === row.analysisId &&
                      selectedCell?.columnKey === column.key;
                    const isEditing =
                      editingCell?.analysisId === row.analysisId &&
                      editingCell?.columnKey === column.key;
                    const isLastRow = index === orderedRows.length - 1;
                    const neighborAddress = (
                      direction: AdvanceDirection,
                    ): CellAddress | null => {
                      const nextRow =
                        orderedRows[
                          index +
                            (direction === "down"
                              ? 1
                              : direction === "up"
                                ? -1
                                : 0)
                        ];
                      const nextColumn =
                        columns[
                          columnIndex +
                            (direction === "right"
                              ? 1
                              : direction === "left"
                                ? -1
                                : 0)
                        ];
                      if (!nextRow || !nextColumn) return null;
                      return {
                        analysisId: nextRow.analysisId,
                        columnKey: nextColumn.key,
                      };
                    };

                    return (
                      <MatrixCell
                        key={column.key}
                        value={cell?.value ?? ""}
                        status={
                          isColumnPrompted(column) &&
                          cell?.status === "idle" &&
                          (row.status === "pending" ||
                            row.status === "in progress")
                            ? "pending"
                            : cell?.status
                        }
                        column={column}
                        badgeColors={badgeColorsByColumn.get(column.key)}
                        isSelected={isSelected}
                        isEditing={isEditing}
                        onSelect={() => onSelectCell(address)}
                        onStartEdit={() => onStartEdit(address)}
                        onCommit={(value) => onCommitEdit(address, value)}
                        onCommitAndAdvance={(value, direction) =>
                          onCommitEdit(
                            address,
                            value,
                            neighborAddress(direction),
                          )
                        }
                        onCancelEdit={onCancelEdit}
                        onExpand={() => onExpandCell(address)}
                        isLastRow={isLastRow}
                      />
                    );
                  })}
                  <td className="bg-background border-r-0" />
                  <td className="bg-background border-r-0" />
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr>
                <td
                  colSpan={columns.length + 4}
                  className="border-border/80 bg-muted/20 border-t p-0"
                >
                  <div className="sticky left-0 w-fit px-3 py-2.5">
                    <button
                      type="button"
                      className="text-muted-foreground hover:text-foreground focus-visible:ring-ring/50 flex items-center gap-1 rounded-md px-1 py-0.5 text-xs font-medium transition-colors focus-visible:ring-[3px] focus-visible:outline-none"
                      onClick={handleAddRow}
                    >
                      {isAddingRow ? (
                        <Loader2Icon className="size-3.5 animate-spin" />
                      ) : (
                        <PlusIcon className="size-3.5" />
                      )}
                      Add row
                    </button>
                  </div>
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>

      <UnsavedChangesDialog
        open={isConfirmingColumnClose}
        onOpenChange={(open) => {
          if (!open) answerConfirmation();
        }}
        onDiscard={discardColumnEditor}
        description="This column has changes that were never saved. Closing the editor loses them."
      />

      <Dialog
        open={columnKeyPendingRemoval !== null}
        onOpenChange={(open) => {
          if (!open) setColumnKeyPendingRemoval(null);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Remove column</DialogTitle>
            <DialogDescription>
              Are you sure you want to remove "
              {columns.find((column) => column.key === columnKeyPendingRemoval)
                ?.label ?? "this column"}
              "? Its values will be removed from every row. This action cannot
              be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setColumnKeyPendingRemoval(null)}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              disabled={hasActiveRows}
              onClick={() => {
                if (columnKeyPendingRemoval) {
                  if (!hasActiveRows) onRemoveColumn(columnKeyPendingRemoval);
                }
                setColumnKeyPendingRemoval(null);
              }}
            >
              Remove column
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
